/**
 * PrivateTemplateEditor — right-side drawer for mutating a workspace's
 * private template directly (not via an estimate).
 *
 * Why this exists: previously the only way to change a private template was
 * delete-and-recreate by saving a new estimate. Contractors needed a way to
 * rename, fix prices, add/remove lines, etc., without leaving the builder
 * and without round-tripping through an estimate.
 *
 * Behavior contract:
 *   - This is the ONLY path that mutates a private template's stored items.
 *   - Inline edits on an estimate built from a private template do NOT flow
 *     back here. That's enforced upstream in SmartTemplates by omitting the
 *     sourceTemplateId stamp on private-template drops.
 *   - Labor cost/rate are read-only, same rule as the line-items table —
 *     labor comes from the overhead calculator in Settings.
 *   - Add Material reuses the same modal as the Library tab; on save it
 *     writes to the workspace pricebook AND inserts a new line in this
 *     template (no zoneId — templates don't have zones).
 */
import React, { useState, useMemo } from 'react';
import { useIdioma } from '../../i18n/idioma';
import { nombreDeDato } from '../../i18n/nombresDeDatos';
import toast from 'react-hot-toast';
import { X, Pencil, Plus, Trash2, Briefcase, Lock, AlertCircle, Sparkles } from 'lucide-react';
import { Button } from '../ui/Button';
import { EmptyState } from '../ui/EmptyState';
import AddMaterialModal from './AddMaterialModal';
import { useUpdatePrivateTemplate, useAddPrivateTemplate } from '../../hooks/useEstimateQueries';
import { useSettings } from '../../contexts/SettingsContext';
import { loadCatalogByIndustry, loadCatalogByCategory, lookupCatalogItem } from '../../data/categories/catalogLoader';
import { upsertWorkspaceCatalogPrice } from '../../services/database';
import type { EstimateItem, EstimateItemType, PrivateTemplate } from '../../types';
import { priceLineByType } from '../../utils/lineItemPricing';

interface LaborOverheadInputs {
    baseLaborCost: number;
    laborBurdenPercent: number;
    overheadPerManHour: number;
    breakEvenRate?: number;
}

export interface PrivateTemplateEditorProps {
    isOpen: boolean;
    template: PrivateTemplate;
    /** T-2: create a brand-new private (line) template instead of editing `template`.
     *  When true, `template` is a blank draft and Save calls addPrivateTemplate. */
    isNew?: boolean;
    /** Workspace target margin percent (0..99). Used to derive rate from cost
     *  when contractor adds a new labor / equipment line. */
    targetMarginPercent: number;
    /** Workspace material markup percent (>= 0). Los materiales se cobran con
     *  ESTE dial, no con el de margen. Faltaba, y por eso el editor cotizaba
     *  cada material con `costo / (1 - margen)` y horneaba un markup de
     *  42,857% en la fila y en la lista de precios del taller. */
    materialMarkupPercent: number;
    /** Workspace overhead inputs — labor lines drop in at this rate. */
    laborOverhead: LaborOverheadInputs;
    onClose: () => void;
}

// Neutral type tag — every line type reads the same; the row's meaning comes
// from its description and numbers, not from a pastel chip.
const TYPE_TAG = 'bg-acero-100 text-acero-700';

function computeOverheadLaborCost(s: LaborOverheadInputs): number {
    if (s.breakEvenRate && s.breakEvenRate > 0) return s.breakEvenRate;
    const burdened = (s.baseLaborCost || 0) * (1 + (s.laborBurdenPercent || 0) / 100);
    return burdened + (s.overheadPerManHour || 0);
}

/**
 * Quita de una partida lo que pertenece a UN presupuesto y no a la plantilla.
 *
 * POR QUÉ: `PrivateTemplate.items` es `any[]` y se guarda literal — lo que
 * entra, sale. Una plantilla se crea muchas veces desde un estimado real, y
 * ese estimado puede traer decisiones tomadas para UN cliente concreto:
 *
 *   · `pricePinned` / `pinnedPercent` / `pinnedMetric` — «a este cliente le
 *     puse 45%». Guardado en la plantilla, ese 45% le gana a la política del
 *     taller en TODOS los presupuestos futuros que la usen, y lo hace en
 *     silencio: la insignia enseña un número que nadie tecleó en ese
 *     presupuesto. Es la misma clase de contaminación que el 42,857% viajando
 *     a la lista de precios, solo que por otra puerta.
 *   · `baseRate` — la línea base ya escalada por el factor de margen neto de
 *     aquel estimado (Parte A.1: `rate = baseRate × k`). Una plantilla debe
 *     RENACER con la política del taller que la usa, no arrastrar el nivel de
 *     precios del trabajo del que salió.
 *
 * Lo que sí se queda: costo, cantidad, unidad, tipo y procedencia. Eso es la
 * plantilla. El precio se vuelve a derivar al soltarla.
 */
export function limpiarDecisionesDePresupuesto(item: any): any {
    const {
        pricePinned: _fijado,
        pinnedPercent: _porcentajeFijado,
        pinnedMetric: _metroFijado,
        baseRate: _precioBase,
        ...resto
    } = item ?? {};
    return resto;
}

const PrivateTemplateEditor: React.FC<PrivateTemplateEditorProps> = ({ isOpen, template, isNew = false, targetMarginPercent, materialMarkupPercent, laborOverhead, onClose }) => {
    const dials = { targetMarginPercent, materialMarkupPercent };
    const { t } = useIdioma();
    const updateMut = useUpdatePrivateTemplate();
    const addMut = useAddPrivateTemplate();
    const saving = isNew ? addMut.isPending : updateMut.isPending;
    const [name, setName] = useState<string>(template.name);
    const [items, setItems] = useState<EstimateItem[]>(() =>
        (Array.isArray(template.items) ? template.items : []).map((i: any) => ({ ...i })) as EstimateItem[]
    );
    const [addMaterialOpen, setAddMaterialOpen] = useState(false);

    // T-2 ($0-category-seed): pull a category's predetermined catalog materials in at
    // $0 so the contractor only sets prices (never types material names). Categories are
    // derived from this workspace's industry catalog snapshot (material-kind rows only).
    const { settings } = useSettings();
    const industry = settings.profile.industryType || 'landscaping';
    const seedCategories = useMemo(() => {
        const set = new Set<string>();
        for (const c of loadCatalogByIndustry(industry)) if (c.default_kind === 'material') set.add(c.category);
        return [...set].sort();
    }, [industry]);
    const humanize = (slug: string) => slug.replace(/[-_]/g, ' ').replace(/\b\w/g, m => m.toUpperCase());

    const seedFromCategory = (category: string) => {
        if (!category) return;
        const mats = loadCatalogByCategory(industry, category).filter(c => c.default_kind === 'material');
        const seen = new Set(items.map(p => p.sourcePricebookId || p.description));
        const additions: EstimateItem[] = mats
            .filter(c => !seen.has(c.id) && !seen.has(c.name))
            .map(c => {
                // Sin costo todavía (el contratista solo pone precios), pero
                // con el markup del taller puesto: si entra en 0, al teclear el
                // costo la fila se revende AL COSTO.
                const precio = priceLineByType('Material', 0, dials);
                return {
                    id: Math.random().toString(36).substr(2, 9),
                    type: 'Material' as EstimateItemType,
                    description: c.name,
                    quantity: 1,
                    cost: 0,
                    ...precio,
                    // El precio de la REGLA es el precio base (k = 1). Aquí vale
                    // 0 porque el costo aún no existe, y aun así se estampa: la
                    // fila debe entrar con línea base propia, no con una que el
                    // solver adivine después (Parte A.6.6).
                    baseRate: precio.rate,
                    total: 0,
                    unit: c.unit,
                    category: c.category,
                    sourcePricebookId: c.id,
                    isOverridden: false,
                };
            });
        if (additions.length === 0) {
            toast(t('plantillaPriv.yaEstan'), { icon: 'ℹ️' });
            return;
        }
        setItems(prev => [...prev, ...additions]);
        toast.success(t('plantillaPriv.agregados', { count: additions.length, cuantos: additions.length, categoria: humanize(category) }));
    };

    // Snap form state back to the template if the drawer is re-opened on a
    // different template (or after a previous edit was cancelled).
    React.useEffect(() => {
        if (isOpen) {
            setName(template.name);
            setItems((Array.isArray(template.items) ? template.items : []).map((i: any) => ({ ...i })) as EstimateItem[]);
        }
    }, [isOpen, template.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleLineChange = (idx: number, updates: Partial<EstimateItem>) => {
        setItems(prev => prev.map((l, i) => {
            if (i !== idx) return l;
            const next = { ...l, ...updates };
            // Recompute rate/total when cost or quantity changes (non-labor only).
            // Cada tipo por su regla —el material por markup— y se guardan los
            // DOS porcentajes: antes solo se reescribía `rate`, así que la fila
            // quedaba con un precio nuevo y unos porcentajes del precio viejo.
            if (next.type !== 'Labor' && ('cost' in updates || 'quantity' in updates)) {
                const { rate, markupPercent, marginPercent } = priceLineByType(next.type, next.cost ?? 0, dials);
                next.rate = rate;
                // Cambió el costo ⇒ cambió el precio que produce la REGLA, o
                // sea la línea base. Aquí no hay factor de margen neto que
                // aplicar (una plantilla no es un estimado), así que base y
                // precio coinciden; escribir solo `rate` dejaría una base
                // vieja apuntando a un costo que ya no existe.
                next.baseRate = rate;
                next.markupPercent = markupPercent;
                next.marginPercent = marginPercent;
                next.total = rate * (next.quantity ?? 0);
            } else if (next.type === 'Labor' && 'quantity' in updates) {
                next.total = (next.rate ?? 0) * (next.quantity ?? 0);
            }
            return next;
        }));
    };

    const handleRemoveLine = (idx: number) => {
        setItems(prev => prev.filter((_, i) => i !== idx));
    };

    const handleAddLabor = () => {
        const laborCost = computeOverheadLaborCost(laborOverhead);
        const { rate, markupPercent, marginPercent } = priceLineByType('Labor', laborCost, dials);
        setItems(prev => [...prev, {
            id: Math.random().toString(36).substr(2, 9),
            type: 'Labor',
            description: t('plantillaPriv.manoDeObraGeneral'),
            quantity: 1,
            cost: laborCost,
            markupPercent,
            marginPercent,
            // Sin costo de mano de obra no hay precio que derivar.
            rate: laborCost > 0 ? rate : laborCost,
            // La mano de obra se cotiza por MARGEN (sobre el precio); ese
            // precio es la línea base de la fila, con k = 1 (Parte A.6.6).
            baseRate: laborCost > 0 ? rate : laborCost,
            total: laborCost > 0 ? rate : laborCost,
            unit: 'HR',
            isOverridden: false,
        }]);
    };

    const handleSave = () => {
        const trimmedName = name.trim();
        if (!trimmedName) {
            toast.error(t('plantillaPriv.error.faltaNombre'));
            return;
        }
        if (items.length === 0) {
            toast.error(t('plantillaPriv.error.sinPartidas'));
            return;
        }
        // Lo que se GUARDA no es lo que se está editando: la plantilla viaja sin
        // los pines ni la línea base del presupuesto del que salió (ver
        // `limpiarDecisionesDePresupuesto`).
        const partidasParaGuardar = items.map(limpiarDecisionesDePresupuesto) as EstimateItem[];
        const onSuccess = () => {
            // T-2: priced, catalog-linked lines (seeded from a category) also fill the
            // workspace pricebook so the price is reusable beyond this template. Idempotent
            // upsert; fire-and-forget so a pricebook hiccup never blocks the template save.
            // sourcePricebookId is overloaded — catalog slug id OR a workspace pricebook
            // row UUID (AddMaterialModal / library drops). Only catalog-resolvable ids may
            // be upserted (others 23503 the catalog_item_id FK); pricebook-row items are
            // already saved as their own rows. Mirrors EstimateCalculator.persistPricebookEdits.
            items
                .filter(it => it.sourcePricebookId && (it.cost ?? 0) > 0 && !!lookupCatalogItem(it.sourcePricebookId))
                .forEach(it => {
                    upsertWorkspaceCatalogPrice(
                        it.sourcePricebookId!,
                        it.cost,
                        { name: it.description, category: it.category || '', unit: it.unit || 'ea' },
                        // El COSTO se persiste —ese es el propósito documentado
                        // de esta escritura—; el porcentaje NO. `it.markupPercent`
                        // es un valor DERIVADO del dial del taller, no algo que
                        // el contratista tecleara para este material; guardarlo
                        // lo convierte en una decisión que le gana al dial en
                        // todos los presupuestos futuros. Es la misma fuga que
                        // la ola 1 cerró en `EstimateCalculator.persistPricebookEdits`,
                        // y este era el otro extremo del mismo tubo
                        // (invariante A.6.2: ningún porcentaje derivado se
                        // escribe jamás en la lista de precios del taller).
                        null,
                    ).catch(err => console.warn('[PrivateTemplateEditor] catalog price upsert failed:', err));
                });
            toast.success(t('plantillaPriv.guardada', {
                count: items.length,
                verbo: isNew ? t('plantillaPriv.verbo.creada') : t('plantillaPriv.verbo.guardada'),
                nombre: trimmedName,
                cuantas: items.length,
            }));
            onClose();
        };
        const onError = (err: any) => toast.error(t('plantillaPriv.errorGuardar', { motivo: err?.message || t('comun.errorDesconocido') }));
        if (isNew) {
            addMut.mutate({ name: trimmedName, items: partidasParaGuardar }, { onSuccess, onError });
        } else {
            updateMut.mutate({ id: template.id, updates: { name: trimmedName, items: partidasParaGuardar } }, { onSuccess, onError });
        }
    };

    if (!isOpen) return null;

    return (
        <>
            {/* Backdrop — click does NOT close (prevents accidental data loss).
                Only the Cancel button or X dismisses the drawer. */}
            <div className="fixed inset-0 z-40 bg-navy-950/50 animate-in fade-in" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} />

            {/* Drawer */}
            <aside className="fixed top-0 right-0 h-full w-full max-w-[560px] bg-white shadow-s3 z-50 flex flex-col animate-in slide-in-from-right">
                {/* Header */}
                <header className="px-6 py-5 border-b border-acero-200 flex items-center justify-between gap-3 shrink-0">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 grid place-items-center bg-acero-100 text-acero-600 rounded-xl shrink-0"><Pencil size={18} /></div>
                        <div className="min-w-0">
                            <p className="label-ui">{isNew ? t('plantillaPriv.modo.crear') : t('plantillaPriv.modo.editar')}</p>
                            <h2 className="text-base font-semibold tracking-tight text-navy-950 truncate">{isNew ? (name || t('plantillaPriv.nombreVacio')) : template.name}</h2>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 text-peligro hover:bg-peligro-bg rounded-lg transition-colors duration-150 shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                        aria-label={t('plantillaPriv.cerrar')}
                    >
                        <X size={18} />
                    </button>
                </header>

                {/* Body */}
                <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar p-5 space-y-4">
                    {/* Name */}
                    <div>
                        <label className="label-ui mb-1.5 block">
                        {t('plantillaPriv.nombre')}
                        </label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="w-full h-10 px-3 bg-white border border-acero-300 rounded-xl text-sm text-navy-950 placeholder:text-acero-400 transition-[border-color,box-shadow] duration-150 focus:outline-none focus:border-navy-600 focus:ring-2 focus:ring-brand-400/55"
                        />
                    </div>

                    {/* T-2: seed predetermined materials from a category at $0 (price-only). */}
                    {seedCategories.length > 0 && (
                        <div>
                            <label className="label-ui mb-1.5 flex items-center gap-1">
                                <Sparkles size={12} /> {t('plantillaPriv.traerCategoria')}
                            </label>
                            <select
                                value=""
                                onChange={(e) => seedFromCategory(e.target.value)}
                                className="w-full h-10 px-3 bg-white border border-acero-300 rounded-xl text-sm text-navy-950 transition-[border-color,box-shadow] duration-150 focus:outline-none focus:border-navy-600 focus:ring-2 focus:ring-brand-400/55"
                            >
                                <option value="">{t('plantillaPriv.traerCategoriaOpcion')}</option>
                                {seedCategories.map(cat => (
                                    <option key={cat} value={cat}>{humanize(cat)}</option>
                                ))}
                            </select>
                            <p className="text-[11px] text-acero-500 mt-1">{t('plantillaPriv.traerCategoriaAyuda')}</p>
                        </div>
                    )}

                    {/* Line items */}
                    <div>
                        <div className="flex items-baseline justify-between mb-2">
                            <h3 className="label-ui">
                                {t('plantillaPriv.partidas')}<span className="font-data">{items.length}</span>)
                            </h3>
                        </div>
                        <ul className="bg-white border border-acero-200 rounded-2xl divide-y divide-acero-100 overflow-hidden">
                            {items.map((line, idx) => (
                                <li key={line.id} className="px-3 py-3 hover:bg-acero-50 transition-colors duration-150 group">
                                    <div className="flex items-start gap-2">
                                        <span className={`shrink-0 label-ui px-1.5 py-0.5 rounded-lg ${TYPE_TAG}`}>
                                            {nombreDeDato(line.type, t)}
                                        </span>
                                        <div className="flex-1 min-w-0">
                                            <input
                                                type="text"
                                                value={line.description}
                                                onChange={(e) => handleLineChange(idx, { description: e.target.value })}
                                                placeholder={t('plantillaPriv.descripcionPartida')}
                                                className="w-full h-8 bg-transparent text-sm font-medium text-navy-950 hover:bg-acero-50 focus:bg-white border border-transparent hover:border-acero-200 rounded-lg px-2 transition-[background-color,border-color,box-shadow] duration-150 focus:outline-none focus:border-navy-600 focus:ring-2 focus:ring-brand-400/55"
                                            />
                                            <div className="flex items-center gap-3 mt-1.5 text-[11px] text-acero-500 pl-2">
                                                <label className="flex items-center gap-1">
                                                    <span className="label-ui">{t('plantillaPriv.cantidad')}</span>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        min="0"
                                                        value={line.quantity}
                                                        onChange={(e) => handleLineChange(idx, { quantity: parseFloat(e.target.value) || 0 })}
                                                        className="w-16 h-7 px-1.5 bg-white border border-acero-300 rounded-lg text-right text-navy-950 font-data tabular-nums transition-[border-color,box-shadow] duration-150 focus:outline-none focus:border-navy-600 focus:ring-2 focus:ring-brand-400/55"
                                                    />
                                                    <span className="label-ui">{line.unit || 'EA'}</span>
                                                </label>
                                                <label className="flex items-center gap-1">
                                                    <span className="label-ui">{t('plantillaPriv.costo')}</span>
                                                    {line.type === 'Labor' ? (
                                                        <span
                                                            className="w-20 h-7 px-1.5 bg-acero-50 text-acero-500 rounded-lg text-right font-data tabular-nums inline-flex items-center justify-end gap-1"
                                                            title={t('plantillaPriv.tip.manoDeObraBloqueada')}
                                                        >
                                                            {(line.cost ?? 0).toFixed(2)}
                                                            <Lock size={10} className="text-acero-400" />
                                                        </span>
                                                    ) : (
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                            value={line.cost ?? 0}
                                                            onChange={(e) => handleLineChange(idx, { cost: parseFloat(e.target.value) || 0 })}
                                                            className={`w-20 h-7 px-1.5 bg-white border rounded-lg text-right text-navy-950 font-data tabular-nums transition-[border-color,box-shadow] duration-150 focus:outline-none focus:border-navy-600 focus:ring-2 focus:ring-brand-400/55 ${(!line.cost || line.cost === 0) ? 'border-brand-400' : 'border-acero-300'}`}
                                                            title={(!line.cost || line.cost === 0) ? t('plantillaPriv.ponPrecio') : undefined}
                                                        />
                                                    )}
                                                </label>
                                                <span className="ml-auto text-[11px] font-data tabular-nums font-medium text-navy-950">
                                                    ${(line.total ?? 0).toFixed(2)}
                                                </span>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => handleRemoveLine(idx)}
                                            className="rp-accion-fila p-1.5 text-acero-400 hover:text-peligro hover:bg-peligro-bg rounded-lg transition-colors duration-150 shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                                            aria-label={t('plantillaPriv.quitarPartidaAria')}
                                            title={t('plantillaPriv.quitarPartidaTip')}
                                        >
                                            <Trash2 size={13} />
                                        </button>
                                    </div>
                                </li>
                            ))}
                            {items.length === 0 && (
                                <li>
                                    <EmptyState
                                        title={t('plantillaPriv.vacio.titulo')}
                                        description={t('plantillaPriv.vacio.texto')}
                                    />
                                </li>
                            )}
                        </ul>
                        {items.length === 0 && (
                            <p className="text-xs text-aviso mt-2 flex items-center gap-1">
                                <AlertCircle size={12} /> {t('plantillaPriv.avisoMinimo')}
                            </p>
                        )}
                    </div>

                    {/* Add-line actions */}
                    <div className="grid grid-cols-2 gap-2">
                        <Button variant="secondary" onClick={() => setAddMaterialOpen(true)} leftIcon={<Plus size={14} />}>
                        {t('plantillaPriv.agregarMaterial')}
                        </Button>
                        <Button variant="outline" onClick={handleAddLabor} leftIcon={<Briefcase size={14} />}>
                            {t('plantillaPriv.agregarManoDeObra')}
                        </Button>
                    </div>

                    <p className="text-[11px] text-acero-500 leading-relaxed">
                        {t('plantillaPriv.notaPie')}
                    </p>
                </div>

                {/* Footer */}
                <footer className="px-5 py-4 border-t border-acero-200 flex items-center justify-between gap-3 shrink-0 bg-acero-50">
                    <Button variant="ghost" onClick={onClose} disabled={saving}>{t('plantillaPriv.cancelar')}</Button>
                    <Button
                        variant="primary"
                        onClick={handleSave}
                        isLoading={saving}
                        disabled={!name.trim() || items.length === 0 || saving}
                    >
                        {saving
                            ? (isNew ? t('plantillaPriv.boton.creando') : t('plantillaPriv.boton.guardando'))
                            : (isNew ? t('plantillaPriv.boton.crear') : t('plantillaPriv.boton.guardar'))}
                    </Button>
                </footer>
            </aside>

            {/* Add Material modal — same component as Library, but here the
                onAdded callback inserts the new item into the template's items
                list (no zoneId, since templates don't carry zones). */}
            <AddMaterialModal
                isOpen={addMaterialOpen}
                targetMarginPercent={targetMarginPercent}
                materialMarkupPercent={materialMarkupPercent}
                onClose={() => setAddMaterialOpen(false)}
                onAdded={(estimateItem) => {
                    const { zoneId: _ignored, ...rest } = estimateItem;
                    setItems(prev => [...prev, rest as EstimateItem]);
                }}
            />
        </>
    );
};

export default PrivateTemplateEditor;
